import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  Search, 
  MapPin, 
  Star, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  MessageSquare,
  TrendingUp,
  LayoutGrid,
  ListPlus,
  Building2,
  Swords
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  cabang_sendiri: { label: "Cabang Sendiri", variant: "default", icon: Building2 },
  kompetitor: { label: "Kompetitor", variant: "secondary", icon: Swords },
};

const HomePage = () => {
  const [url, setUrl] = useState("");
  const [storeCode, setStoreCode] = useState("");
  const [category, setCategory] = useState("cabang_sendiri"); // "cabang_sendiri" | "kompetitor"
  const [mode, setMode] = useState("single"); // "single" | "bulk"
  const [bulkText, setBulkText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all"); // "all" | "cabang_sendiri" | "kompetitor"
  const navigate = useNavigate();

  const fetchJobs = async (filterOverride) => {
    setLoadingJobs(true);
    const filterToUse = filterOverride !== undefined ? filterOverride : historyFilter;
    try {
      const params = filterToUse !== "all" ? { category: filterToUse } : {};
      const response = await axios.get(`${API}/scrape-jobs`, { params });
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Gagal memuat history scraping");
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // Auto refresh every 5 seconds
    const interval = setInterval(() => fetchJobs(), 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      toast.error("Mohon masukkan URL Google Maps");
      return;
    }
    
    if (!url.includes("google.com/maps")) {
      toast.error("URL tidak valid. Mohon masukkan URL Google Maps yang benar");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/scrape`, {
        url,
        store_code: storeCode.trim() || undefined,
        category,
      });
      toast.success("Scraping dimulai! Mohon tunggu...");
      setUrl("");
      setStoreCode("");
      
      // Refresh jobs list
      await fetchJobs();
      
      // Navigate to job details after 2 seconds
      setTimeout(() => {
        navigate(`/job/${response.data.id}`);
      }, 2000);
      
    } catch (error) {
      console.error("Error starting scrape:", error);
      toast.error(error.response?.data?.detail || "Gagal memulai scraping");
    } finally {
      setIsLoading(false);
    }
  };

  // Parses lines like: "M01, https://www.google.com/maps/place/..."
  // Also accepts just a bare URL per line (store_code becomes optional).
  const parseBulkLines = (text) => {
    return text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const parts = line.split(",").map((p) => p.trim());
        if (parts.length >= 2 && parts[1].includes("google.com/maps")) {
          return { store_code: parts[0] || undefined, url: parts.slice(1).join(",") };
        }
        // Fallback: whole line is just a URL
        return { store_code: undefined, url: line };
      });
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    const items = parseBulkLines(bulkText);

    if (items.length === 0) {
      toast.error("Mohon masukkan minimal satu baris (Kode Toko, URL)");
      return;
    }

    const invalid = items.filter((it) => !it.url.includes("google.com/maps"));
    if (invalid.length > 0) {
      toast.error(`Ada ${invalid.length} baris dengan URL Google Maps yang tidak valid`);
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/scrape-bulk`, { items, category });
      toast.success(`${items.length} scraping toko dimulai! Cek Dashboard 33 Cabang untuk progressnya.`);
      setBulkText("");
      await fetchJobs();
      setTimeout(() => navigate(category === "cabang_sendiri" ? "/stores" : "/"), 1500);
    } catch (error) {
      console.error("Error starting bulk scrape:", error);
      toast.error(error.response?.data?.detail || "Gagal memulai bulk scraping");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Menunggu", variant: "secondary", icon: Clock },
      processing: { label: "Memproses", variant: "default", icon: Loader2 },
      completed: { label: "Selesai", variant: "success", icon: CheckCircle2 },
      failed: { label: "Gagal", variant: "destructive", icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <MapPin className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Google Maps Review Scraper
            </h1>
          </div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Extract semua review dari Google Maps dengan mudah. Masukkan URL tempat yang ingin Anda scrape.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate("/stores")}
            data-testid="goto-stores-dashboard-btn"
            className="gap-2"
          >
            <LayoutGrid className="w-4 h-4" />
            Lihat Dashboard 33 Cabang
          </Button>
        </div>

        {/* Input Form */}
        <Card className="shadow-lg border-2 border-blue-100" data-testid="scrape-form-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Scrape Google Maps Reviews
                </CardTitle>
                <CardDescription>
                  {mode === "single"
                    ? "Paste URL Google Maps (contoh: https://www.google.com/maps/place/...)"
                    : "Satu baris per toko: KODE_TOKO, URL Google Maps"}
                </CardDescription>
              </div>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={mode === "single" ? "default" : "ghost"}
                  onClick={() => setMode("single")}
                  data-testid="mode-single-btn"
                >
                  1 Toko
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={mode === "bulk" ? "default" : "ghost"}
                  onClick={() => setMode("bulk")}
                  data-testid="mode-bulk-btn"
                >
                  <ListPlus className="w-4 h-4 mr-1" />
                  Bulk (Banyak Toko)
                </Button>
              </div>
            </div>

            {/* Category selector - applies to whichever mode is active */}
            <div className="flex items-center gap-2 pt-3">
              <span className="text-sm text-gray-500">Kategori:</span>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={category === "cabang_sendiri" ? "default" : "ghost"}
                  onClick={() => setCategory("cabang_sendiri")}
                  data-testid="category-own-btn"
                  className="gap-1.5"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  Cabang Sendiri
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={category === "kompetitor" ? "default" : "ghost"}
                  onClick={() => setCategory("kompetitor")}
                  data-testid="category-competitor-btn"
                  className="gap-1.5"
                >
                  <Swords className="w-3.5 h-3.5" />
                  Kompetitor
                </Button>
              </div>
              {category === "kompetitor" && (
                <span className="text-xs text-gray-400">
                  (tidak akan masuk ke Dashboard 33 Cabang)
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {mode === "single" ? (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    data-testid="store-code-input"
                    type="text"
                    placeholder={category === "cabang_sendiri" ? "Kode Toko (opsional, mis. M01)" : "Label (opsional, mis. Kompetitor-A)"}
                    value={storeCode}
                    onChange={(e) => setStoreCode(e.target.value)}
                    className="sm:w-56 text-base"
                    disabled={isLoading}
                  />
                  <Input
                    data-testid="gmaps-url-input"
                    type="url"
                    placeholder="https://www.google.com/maps/place/..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="flex-1 text-base"
                    disabled={isLoading}
                  />
                  <Button 
                    data-testid="start-scraping-btn"
                    type="submit" 
                    disabled={isLoading}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Memproses...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Mulai Scraping
                      </>
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleBulkSubmit} className="space-y-3">
                <Textarea
                  data-testid="bulk-urls-textarea"
                  placeholder={"M01, https://www.google.com/maps/place/...\nM02, https://www.google.com/maps/place/...\nM03, https://www.google.com/maps/place/..."}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  className="min-h-[160px] font-mono text-sm"
                  disabled={isLoading}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Tips: siapkan daftar 33 link Google Maps toko dulu di spreadsheet, lalu paste ke sini (format: kode toko, koma, URL).
                    Scraping berjalan bergantian di background (2 toko sekaligus) supaya server tidak overload.
                  </p>
                  <Button
                    data-testid="start-bulk-scraping-btn"
                    type="submit"
                    disabled={isLoading}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shrink-0"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Memproses...
                      </>
                    ) : (
                      <>
                        <ListPlus className="w-4 h-4 mr-2" />
                        Mulai Bulk Scraping
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Statistics */}
        {jobs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Scraping</p>
                    <p className="text-3xl font-bold text-blue-900">{jobs.length}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Selesai</p>
                    <p className="text-3xl font-bold text-green-900">
                      {jobs.filter(j => j.status === 'completed').length}
                    </p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total Reviews</p>
                    <p className="text-3xl font-bold text-purple-900">
                      {jobs.reduce((acc, job) => acc + (job.total_reviews_scraped || 0), 0)}
                    </p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Jobs History */}
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-2xl font-bold text-gray-800">History Scraping</h2>
            <div className="flex items-center gap-2">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={historyFilter === "all" ? "default" : "ghost"}
                  onClick={() => setHistoryFilter("all")}
                  data-testid="history-filter-all-btn"
                >
                  Semua
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={historyFilter === "cabang_sendiri" ? "default" : "ghost"}
                  onClick={() => setHistoryFilter("cabang_sendiri")}
                  data-testid="history-filter-own-btn"
                  className="gap-1.5"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  Cabang Sendiri
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={historyFilter === "kompetitor" ? "default" : "ghost"}
                  onClick={() => setHistoryFilter("kompetitor")}
                  data-testid="history-filter-competitor-btn"
                  className="gap-1.5"
                >
                  <Swords className="w-3.5 h-3.5" />
                  Kompetitor
                </Button>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fetchJobs()}
                disabled={loadingJobs}
              >
                {loadingJobs ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Refresh"
                )}
              </Button>
            </div>
          </div>

          {loadingJobs && jobs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : jobs.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center space-y-2">
                  <MapPin className="w-16 h-16 mx-auto text-gray-300" />
                  <p className="text-gray-500 text-lg">Belum ada scraping yang dilakukan</p>
                  <p className="text-gray-400 text-sm">Mulai scraping dengan memasukkan URL Google Maps di atas</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="jobs-list">
              {jobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-500"
                  onClick={() => navigate(`/job/${job.id}`)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            {getStatusBadge(job.status)}
                            {job.store_code && (
                              <Badge variant="outline" className="font-mono">{job.store_code}</Badge>
                            )}
                            {job.category && (() => {
                              const catConfig = CATEGORY_LABELS[job.category] || CATEGORY_LABELS.cabang_sendiri;
                              const CatIcon = catConfig.icon;
                              return (
                                <Badge variant={catConfig.variant} className="flex items-center gap-1">
                                  <CatIcon className="w-3 h-3" />
                                  {catConfig.label}
                                </Badge>
                              );
                            })()}
                            <span className="text-sm text-gray-500">
                              {formatDate(job.created_at)}
                            </span>
                          </div>
                          
                          {job.place_name && (
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                              <MapPin className="w-5 h-5 text-blue-600" />
                              {job.place_name}
                            </h3>
                          )}
                          
                          {job.place_address && (
                            <p className="text-sm text-gray-600">{job.place_address}</p>
                          )}
                          
                          <p className="text-sm text-gray-500 font-mono truncate">
                            {job.url}
                          </p>
                        </div>
                        
                        {job.place_rating && (
                          <div className="text-right">
                            <div className="flex items-center gap-1 justify-end">
                              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                              <span className="text-2xl font-bold text-gray-900">
                                {job.place_rating}
                              </span>
                            </div>
                            {job.place_total_reviews && (
                              <p className="text-xs text-gray-500 mt-1">
                                {job.place_total_reviews.toLocaleString()} reviews
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {job.status === 'completed' && (
                        <div className="flex items-center gap-4 pt-2 border-t">
                          <div className="flex items-center gap-2 text-sm">
                            <MessageSquare className="w-4 h-4 text-purple-600" />
                            <span className="font-medium">{job.total_reviews_scraped}</span>
                            <span className="text-gray-500">reviews di-scrape</span>
                          </div>
                        </div>
                      )}
                      
                      {job.status === 'failed' && job.error_message && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-sm text-red-600 break-words whitespace-pre-wrap">Error: {job.error_message}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;